(() => {
  if (customElements.get('integrale-bike-finder')) return;

  class IntegraleBikeFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.init();
    }

    init() {
      const root = this;
      const marquesScript = root.querySelector('#marques');
      const modelesScript = root.querySelector('#modeles');
      const productsScript = root.querySelector('#integrale_products');
      const configScript = root.querySelector('#integrale_finder_config');
      if (!marquesScript || !modelesScript) return;

      let marques = [];
      let modeles = [];
      let anneeOptions = [];
      let allProductsCache = null;
      let allProductsPromise = null;
      let hasEmbeddedProducts = false;
      let navscriptBlockedReason = null;
      let matchedProductUrl = null;
      let activeSelect = null;
      let helpUrl = '';
      let catalogCollection = '';
      let catalogPages = 1;
      let catalogFullyLoaded = true;
      let catalogPagesPromise = null;
      let shopRootUrl = '/';

      const state = {
        marque: null,
        modeleNom: null,
        modele: null,
        sous_modele: null,
        annee: null,
      };

      const debugEnabled = Boolean(window.INTEGRALE_BIKE_FINDER_DEBUG);

      const debugLog = (label, payload) => {
        if (!debugEnabled) return;
        if (typeof payload === 'undefined') {
          console.log('[Integrale Bike Finder]', label);
          return;
        }
        console.log('[Integrale Bike Finder]', label, payload);
      };

      const parseMetaJsonScript = (node, label) => {
        if (!node) return { data: [], error: 'missing' };
        const raw = (node.textContent || '').trim();
        if (!raw) return { data: [], error: null };
        try {
          const parsed = JSON.parse(raw);
          return { data: Array.isArray(parsed) ? parsed : [], error: null };
        } catch (error) {
          console.error(`Impossible de parser #${label}:`, error, raw.slice(0, 200));
          return { data: [], error: 'parse' };
        }
      };

      let marquesParseError = null;
      let modelesParseError = null;

      marques = parseMetaJsonScript(marquesScript, 'marques').data;
      marquesParseError = parseMetaJsonScript(marquesScript, 'marques').error;

      const modelesResult = parseMetaJsonScript(modelesScript, 'modeles');
      modeles = modelesResult.data;
      modelesParseError = modelesResult.error;

      const embeddedProducts = productsScript
        ? parseMetaJsonScript(productsScript, 'integrale_products').data
        : [];
      if (productsScript) {
        allProductsCache = embeddedProducts;
        hasEmbeddedProducts = true;
      }

      if (configScript) {
        try {
          const config = JSON.parse((configScript.textContent || '{}').trim() || '{}');
          helpUrl = (config && config.help_url) || '';
          catalogCollection = (config && config.catalog_collection) || '';
          catalogPages = Number((config && config.catalog_pages) || 1) || 1;
          catalogFullyLoaded = catalogPages <= 1 || !catalogCollection;
          shopRootUrl = (config && config.root_url) || '/';
        } catch (error) {
          console.error('Impossible de parser #integrale_finder_config:', error);
        }
      }

      const normalize = (value) =>
        (value || '')
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim();

      marques.sort((a, b) => normalize(a && a.nom).localeCompare(normalize(b && b.nom), 'fr'));

      const escapeHtml = (value) =>
        String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

      const decodeHtmlEntities = (value) => {
        const text = String(value || '');
        if (!text.includes('&')) return text;
        const node = document.createElement('textarea');
        node.innerHTML = text;
        return node.value;
      };

      const getIntegraleString = (key, fallback = '') =>
        decodeHtmlEntities((window.slicyStrings && window.slicyStrings.integrale && window.slicyStrings.integrale[key]) || fallback);

      const getFieldConfig = (name) => {
        const field = root.querySelector(`[data-bike-finder-field="${name}"]`);
        if (!field) return null;
        return {
          name,
          field,
          trigger: field.querySelector(`[data-bike-finder-trigger="${name}"]`),
          popup: field.querySelector('.integrale-pdp__popup'),
          panel: field.querySelector('[data-bike-finder-popup-panel]'),
          searchInput: field.querySelector('[data-bike-finder-search]'),
          listNode: field.querySelector('[data-bike-finder-list]'),
          emptyNode: field.querySelector('[data-bike-finder-empty]'),
          labelNode: field.querySelector('[data-bike-finder-label]'),
          helpNode: field.querySelector('[data-bike-finder-help]'),
        };
      };

      const marqueSelect = getFieldConfig('marque');
      const modeleSelect = getFieldConfig('modele');
      const sousModeleSelect = getFieldConfig('sous_modele');
      const anneeSelect = getFieldConfig('annee');
      if (!marqueSelect || !modeleSelect || !sousModeleSelect || !anneeSelect) return;

      const getIntegralePdpRoot = () => root.closest('.integrale-pdp') || document;

      const getRedirectButton = () => getIntegralePdpRoot().querySelector('[data-integrale-redirect-submit]');

      const toMetaobjectId = (value) => {
        if (!value) return '';
        const str = String(value).trim();
        return str.includes('/') ? str.split('/').pop() : str;
      };

      const supplementCatalogFromProducts = (products) => {
        if (!Array.isArray(products) || !products.length) return;

        const marqueSeen = new Set(marques.map((item) => toMetaobjectId(item && item.id)).filter(Boolean));
        const modeleSeen = new Set(modeles.map((item) => toMetaobjectId(item && item.id)).filter(Boolean));

        products.forEach((product) => {
          const marqueId = toMetaobjectId(product && product.marque_id);
          const marqueNom = String((product && product.marque) || '').trim();
          if (marqueId) {
            if (!marqueSeen.has(marqueId)) {
              marqueSeen.add(marqueId);
              marques.push({ id: marqueId, nom: marqueNom || marqueId });
            } else if (marqueNom) {
              const marqueEntry = marques.find((item) => toMetaobjectId(item && item.id) === marqueId);
              if (marqueEntry && !String(marqueEntry.nom || '').trim()) marqueEntry.nom = marqueNom;
            }
          }

          const modeleId = toMetaobjectId(product && product.modele_id);
          const modeleNom = String((product && product.modele) || '').trim();
          if (modeleId) {
            if (!modeleSeen.has(modeleId)) {
              modeleSeen.add(modeleId);
              modeles.push({
                id: modeleId,
                nom: modeleNom || modeleId,
                marque_id: marqueId,
                sous_modele: '',
              });
            } else {
              const modeleEntry = modeles.find((item) => toMetaobjectId(item && item.id) === modeleId);
              if (modeleEntry) {
                if (!String(modeleEntry.nom || '').trim() && modeleNom) modeleEntry.nom = modeleNom;
                if (!toMetaobjectId(modeleEntry.marque_id) && marqueId) modeleEntry.marque_id = marqueId;
              }
            }
          }
        });

        marques.sort((a, b) => normalize(a && a.nom).localeCompare(normalize(b && b.nom), 'fr'));
      };

      if (embeddedProducts.length) supplementCatalogFromProducts(embeddedProducts);

      const resolveModeleNom = (entry) => {
        const fromEntry = String((entry && entry.nom) || '').trim();
        if (fromEntry) return fromEntry;
        const modeleId = toMetaobjectId(entry && entry.id);
        if (!modeleId || !allProductsCache) return '';
        const match = allProductsCache.find((product) => toMetaobjectId(product && product.modele_id) === modeleId);
        return String((match && match.modele) || '').trim();
      };

      const uniqueStrings = (values) => {
        const seen = Object.create(null);
        return values.filter((value) => {
          const key = String(value || '').trim();
          if (!key || seen[key]) return false;
          seen[key] = true;
          return true;
        });
      };

      const toOptionObjects = (values) =>
        uniqueStrings(values).map((value) => ({
          id: String(value),
          nom: String(value),
        }));

      const extractAnneeLabel = (entry) => {
        if (entry == null || entry === '') return '';
        if (typeof entry === 'string' || typeof entry === 'number') return String(entry).trim();
        if (typeof entry === 'object') {
          return String(
            entry.nom || entry.annee || entry.label || entry.name || entry.title || entry.value || ''
          ).trim();
        }
        return '';
      };

      const extractAnneeOptions = (product) => {
        const raw = product && product.annee;
        if (raw == null || raw === '') return [];
        const entries = Array.isArray(raw) ? raw : [raw];
        return toOptionObjects(entries.map(extractAnneeLabel));
      };

      const productMatchesAnnee = (product, anneeNom) => {
        if (!anneeNom) return true;
        return extractAnneeOptions(product).some((item) => normalize(item.nom) === normalize(anneeNom));
      };

      const getModeleEntriesForMarque = () => {
        if (!state.marque || !state.marque.id) return [];
        const selectedMarqueId = toMetaobjectId(state.marque.id);
        return modeles.filter((item) => toMetaobjectId(item && item.marque_id) === selectedMarqueId);
      };

      const getModeleEntriesForSelection = () => {
        if (!state.modeleNom) return [];
        return getModeleEntriesForMarque().filter(
          (item) => normalize(resolveModeleNom(item)) === normalize(state.modeleNom)
        );
      };

      const getUniqueModeleNomOptions = () => {
        const entries = getModeleEntriesForMarque();
        const seen = Object.create(null);
        const options = [];
        entries.forEach((entry) => {
          const nom = resolveModeleNom(entry);
          if (!nom) return;
          const key = normalize(nom);
          if (seen[key]) return;
          seen[key] = true;
          options.push({ id: key, nom });
        });
        return options.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
      };

      const getSousModeleOptions = () => {
        return getModeleEntriesForSelection()
          .filter((entry) => String((entry && entry.sous_modele) || '').trim())
          .map((entry) => ({
            id: String(entry.id),
            nom: String(entry.sous_modele).trim(),
            modele_id: String(entry.id),
          }))
          .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
      };

      const needsSousModeleSelect = () => getSousModeleOptions().length > 1;

      const resolveModeleFromSelection = () => {
        const entries = getModeleEntriesForSelection();
        if (!entries.length) {
          state.modele = null;
          return;
        }

        if (needsSousModeleSelect()) {
          if (!state.sous_modele || !state.sous_modele.modele_id) {
            state.modele = null;
            return;
          }
          const match = entries.find((entry) => String(entry.id) === String(state.sous_modele.modele_id));
          state.modele = match
            ? { id: match.id, nom: match.nom, sous_modele: match.sous_modele || '' }
            : null;
          return;
        }

        const entry = entries[0];
        state.modele = entry ? { id: entry.id, nom: entry.nom, sous_modele: entry.sous_modele || '' } : null;
      };

      const setTriggerState = (config, enabled) => {
        config.trigger.disabled = !enabled;
        config.trigger.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        config.trigger.classList.toggle('opacity-30', !enabled);
      };

      const setFieldVisible = (config, visible) => {
        config.field.hidden = !visible;
        if (!visible) {
          setTriggerState(config, false);
          closePopup(config, false);
        }
      };

      const setLabel = (config, value) => {
        config.labelNode.textContent = value || config.trigger.dataset.bikeFinderLabelDefault || '';
      };

      const renderHelp = (config) => {
        if (!config.helpNode) return;
        if (!helpUrl) {
          config.helpNode.hidden = true;
          config.helpNode.innerHTML = '';
          return;
        }
        const helpHtml = getIntegraleString('help_link_html');
        if (!helpHtml) {
          config.helpNode.hidden = true;
          return;
        }
        config.helpNode.innerHTML = helpHtml.replace(/\{\{\s*url\s*\}\}/g, escapeHtml(helpUrl));
        config.helpNode.hidden = false;
      };

      const getOptions = (name) => {
        if (name === 'marque') return marques;
        if (name === 'modele') return getUniqueModeleNomOptions();
        if (name === 'sous_modele') return getSousModeleOptions();
        if (name === 'annee') return anneeOptions;
        return [];
      };

      const renderOptions = (config, query) => {
        const filtered = getOptions(config.name).filter((item) =>
          normalize(item && item.nom).includes(normalize(query))
        );
        config.listNode.innerHTML = filtered
          .map((item) => {
            const escaped = escapeHtml((item && item.nom) || '');
            const optionId = escapeHtml(
              config.name === 'sous_modele' && item.modele_id ? item.modele_id : item && item.id ? item.id : ''
            );
            return `<li><button type="button" class="integrale-pdp__popup-option" data-option-id="${optionId}" data-option-name="${escaped}">${escaped}</button></li>`;
          })
          .join('');
        config.emptyNode.hidden = filtered.length > 0;
        renderHelp(config);
      };

      const closePopup = (config, shouldFocusTrigger) => {
        if (!config || config.popup.hidden) return;
        config.popup.hidden = true;
        config.trigger.setAttribute('aria-expanded', 'false');
        if (activeSelect === config.name) activeSelect = null;
        if (shouldFocusTrigger) config.trigger.focus();
      };

      const closeAllPopups = () => {
        [marqueSelect, modeleSelect, sousModeleSelect, anneeSelect].forEach((config) => closePopup(config, false));
      };

      const openPopup = (config) => {
        if (config.trigger.disabled) return;
        if (activeSelect && activeSelect !== config.name) closeAllPopups();
        config.popup.hidden = false;
        config.trigger.setAttribute('aria-expanded', 'true');
        if (config.searchInput) config.searchInput.value = '';
        renderOptions(config, '');
        activeSelect = config.name;
        if (config.searchInput) setTimeout(() => config.searchInput.focus(), 0);
      };

      const matchMessageEl = root.querySelector('[data-integrale-match-message]');

      const clearMatchMessage = () => {
        if (!matchMessageEl) return;
        matchMessageEl.hidden = true;
        matchMessageEl.textContent = '';
      };

      const showMatchMessage = (reason) => {
        if (!matchMessageEl) return;
        const messages = {
          none: getIntegraleString('noProductMatch'),
          error: getIntegraleString('catalogLoadError'),
          meta_empty: getIntegraleString('catalogMetaEmpty'),
          parse_error: getIntegraleString('catalogDataParseError'),
          password: getIntegraleString('catalogPasswordBlocked'),
        };
        const message = messages[reason] || messages.none;
        if (!message) return;
        matchMessageEl.textContent = message;
        matchMessageEl.hidden = false;
      };

      const resetRedirect = () => {
        matchedProductUrl = null;
        const button = getRedirectButton();
        if (!button) return;
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        delete button.dataset.productUrl;
      };

      const updateRedirectButton = (products) => {
        const button = getRedirectButton();
        if (!button) return;

        if (!state.marque || !state.modele || !state.annee || !Array.isArray(products)) {
          resetRedirect();
          return;
        }

        const product = findProductForRedirect(products);
        if (!product || !product.url) {
          resetRedirect();
          return;
        }

        matchedProductUrl = product.url;
        button.disabled = false;
        button.setAttribute('aria-disabled', 'false');
        button.dataset.productUrl = product.url;
      };

      const resetAnnee = () => {
        state.annee = null;
        anneeOptions = [];
        delete anneeSelect.trigger.dataset.selectedAnnee;
        setLabel(anneeSelect, '');
        setTriggerState(anneeSelect, false);
        closePopup(anneeSelect, false);
        resetRedirect();
        clearMatchMessage();
      };

      const resetSousModele = () => {
        state.sous_modele = null;
        delete sousModeleSelect.trigger.dataset.selectedSousModeleId;
        setLabel(sousModeleSelect, '');
        setFieldVisible(sousModeleSelect, false);
        state.modele = null;
        resetAnnee();
      };

      const resetModele = () => {
        state.modeleNom = null;
        state.modele = null;
        delete modeleSelect.trigger.dataset.selectedModeleNom;
        setLabel(modeleSelect, '');
        setTriggerState(modeleSelect, false);
        closePopup(modeleSelect, false);
        resetSousModele();
      };

      const buildShopPath = (path) => {
        const root = String(shopRootUrl || '/');
        const normalizedPath = String(path || '').replace(/^\//, '');
        if (root === '/') return `/${normalizedPath}`;
        return `${root.replace(/\/$/, '')}/${normalizedPath}`;
      };

      const getMatchingProducts = (products) => {
        const targetModeleId = toMetaobjectId(state.modele && state.modele.id);
        const targetMarqueId = toMetaobjectId(state.marque && state.marque.id);
        const targetMarque = normalize(state.marque && state.marque.nom);
        const targetModele = normalize(state.modele && state.modele.nom);

        return products.filter((product) => {
          const productModeleId = toMetaobjectId(product && product.modele_id);
          const productModele = normalize(product && product.modele);
          const productMarqueId = toMetaobjectId(product && product.marque_id);
          const productMarque = normalize(product && product.marque);

          const modeleMatches = targetModeleId && productModeleId
            ? productModeleId === targetModeleId
            : productModele === targetModele;

          const marqueMatches = targetMarqueId && productMarqueId
            ? productMarqueId === targetMarqueId
            : !productMarque || productMarque === targetMarque;

          return modeleMatches && marqueMatches;
        });
      };

      const findProductForRedirect = (products) => {
        const matches = getMatchingProducts(products).filter((product) =>
          productMatchesAnnee(product, state.annee && state.annee.nom)
        );
        return matches[0] || null;
      };

      const enrichAnneeFromSelection = () => {
        resolveModeleFromSelection();
        resetAnnee();

        if (!state.marque || !state.modele) {
          return;
        }

        fetchAllProducts().then((products) => {
          if (!products.length) {
            showMatchMessage(navscriptBlockedReason === 'password' ? 'password' : 'error');
            return;
          }

          const matches = getMatchingProducts(products);
          if (!matches.length) {
            showMatchMessage('none');
            debugLog('Aucun produit trouve', { modele_id: state.modele.id });
            return;
          }

          const aggregatedAnnees = [];
          matches.forEach((product) => {
            aggregatedAnnees.push(...extractAnneeOptions(product).map((item) => item.nom));
          });
          anneeOptions = toOptionObjects(aggregatedAnnees).sort((a, b) =>
            String(b.nom).localeCompare(String(a.nom), 'fr', { numeric: true })
          );

          debugLog('Annees disponibles', {
            modele_id: state.modele.id,
            count: anneeOptions.length,
            annees: anneeOptions.map((item) => item.nom),
          });

          setTriggerState(anneeSelect, anneeOptions.length > 0);
          if (!anneeOptions.length) showMatchMessage('none');
        });
      };

      const handleModeleNomSelected = (selectedName) => {
        state.modeleNom = selectedName;
        modeleSelect.trigger.dataset.selectedModeleNom = selectedName;
        setLabel(modeleSelect, selectedName);
        resetSousModele();

        const sousOptions = getSousModeleOptions();
        if (needsSousModeleSelect()) {
          setFieldVisible(sousModeleSelect, true);
          setTriggerState(sousModeleSelect, true);
          return;
        }

        resolveModeleFromSelection();
        enrichAnneeFromSelection();
      };

      const handleSousModeleSelected = (selectedName, selectedId) => {
        state.sous_modele = { id: selectedId, nom: selectedName, modele_id: selectedId };
        sousModeleSelect.trigger.dataset.selectedSousModeleId = selectedId;
        setLabel(sousModeleSelect, selectedName);
        resolveModeleFromSelection();
        enrichAnneeFromSelection();
      };

      const navscriptLoadingEl = root.querySelector('[data-integrale-navscript-loading]');
      const navscriptLoadingLabelEl = root.querySelector('[data-integrale-navscript-loading-label]');
      const navscriptLoadingProgressEl = root.querySelector('[data-integrale-navscript-loading-progress]');
      const bikeFinderFieldsEl = root.querySelector('[data-integrale-bike-finder-fields]');
      const navscriptProgressTemplate = getIntegraleString('catalogLoadingPage', 'Page {{ current }} / {{ total }}');

      if (navscriptLoadingLabelEl) {
        const loadingLabel = getIntegraleString('catalogLoading');
        if (loadingLabel) navscriptLoadingLabelEl.textContent = loadingLabel;
      }

      const setNavscriptLoading = (active, progress) => {
        if (navscriptLoadingEl) navscriptLoadingEl.hidden = !active;
        if (bikeFinderFieldsEl) bikeFinderFieldsEl.setAttribute('aria-busy', active ? 'true' : 'false');
        if (active) closeAllPopups();

        if (!navscriptLoadingProgressEl) return;
        if (progress && progress.current && progress.total && progress.total > 1) {
          navscriptLoadingProgressEl.textContent = navscriptProgressTemplate
            .replace(/\{\{\s*current\s*\}\}/g, String(progress.current))
            .replace(/\{\{\s*total\s*\}\}/g, String(progress.total));
          navscriptLoadingProgressEl.hidden = false;
        } else {
          navscriptLoadingProgressEl.textContent = '';
          navscriptLoadingProgressEl.hidden = true;
        }
      };

      const isPasswordStorefrontHtml = (html) =>
        /storefront[_-]?password|password-page|password_form/i.test(html) ||
        /boutique est protégée|password-protected|enter store password|saisir le mot de passe/i.test(html);

      const parseNavscriptPayload = (text) => {
        if (isPasswordStorefrontHtml(text)) {
          return { products: [], pagination: { pages: 1 }, collection: null, blocked: 'password' };
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const script = doc.querySelector('script[type="application/json"]');
        if (!script || !script.textContent) {
          return {
            products: [],
            pagination: { pages: 1 },
            collection: null,
            blocked: isPasswordStorefrontHtml(text) ? 'password' : null,
          };
        }

        try {
          const payload = JSON.parse(script.textContent.trim());
          return {
            products: payload && Array.isArray(payload.products) ? payload.products : [],
            pagination: (payload && payload.pagination) || { pages: 1 },
            collection: (payload && payload.collection) || null,
            blocked: null,
          };
        } catch (error) {
          console.error('JSON navscript invalide:', error);
          return { products: [], pagination: { pages: 1 }, collection: null, blocked: null };
        }
      };

      const parseIntegraleCatalogPayload = (text) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const script = doc.querySelector('script[type="application/json"]');
        if (!script || !script.textContent) {
          return { products: [], pagination: { pages: 1 } };
        }

        try {
          const payload = JSON.parse(script.textContent.trim());
          return {
            products: payload && Array.isArray(payload.products) ? payload.products : [],
            pagination: (payload && payload.pagination) || { pages: 1 },
          };
        } catch (error) {
          console.error('JSON integrale-catalog invalide:', error);
          return { products: [], pagination: { pages: 1 } };
        }
      };

      const mergeCatalogProducts = (extraProducts) => {
        if (!Array.isArray(extraProducts) || !extraProducts.length) return;
        const seen = new Set((allProductsCache || []).map((product) => product && product.url).filter(Boolean));
        const merged = [...(allProductsCache || [])];

        extraProducts.forEach((product) => {
          if (!product || !product.url || seen.has(product.url)) return;
          seen.add(product.url);
          merged.push(product);
        });

        allProductsCache = merged;
        supplementCatalogFromProducts(extraProducts);
      };

      const loadRemainingCatalogPages = () => {
        if (catalogFullyLoaded || !catalogCollection || catalogPages <= 1) {
          return Promise.resolve(allProductsCache || []);
        }
        if (catalogPagesPromise) return catalogPagesPromise;

        catalogPagesPromise = (async () => {
          try {
            for (let page = 2; page <= catalogPages; page += 1) {
              const url = buildShopPath(
                `collections/${encodeURIComponent(catalogCollection)}?view=integrale-catalog&page=${page}`
              );
              const response = await fetch(url, { credentials: 'same-origin' });
              if (!response.ok) continue;

              const payload = parseIntegraleCatalogPayload(await response.text());
              mergeCatalogProducts(payload.products);
            }

            catalogFullyLoaded = true;
            return allProductsCache || [];
          } catch (error) {
            console.error('Impossible de charger le catalogue intégrale paginé:', error);
            return allProductsCache || [];
          } finally {
            catalogPagesPromise = null;
          }
        })();

        return catalogPagesPromise;
      };

      const fetchAllProducts = () => {
        if (hasEmbeddedProducts) {
          return loadRemainingCatalogPages().then(() => allProductsCache || []);
        }
        if (allProductsCache) return Promise.resolve(allProductsCache);
        if (allProductsPromise) return allProductsPromise;

        allProductsPromise = (async () => {
          let page = 1;
          let totalPages = 1;
          let products = [];

          setNavscriptLoading(true);

          try {
            do {
              setNavscriptLoading(true, { current: page, total: totalPages });
              const url = buildShopPath(
                page === 1 ? 'collections/all?view=navscript' : `collections/all?view=navscript&page=${page}`
              );
              const response = await fetch(url, { credentials: 'same-origin' });
              if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

              const payload = parseNavscriptPayload(await response.text());
              if (payload.blocked === 'password') {
                navscriptBlockedReason = 'password';
                break;
              }

              if (page === 1) {
                totalPages = Number((payload.pagination && payload.pagination.pages) || 1);
                setNavscriptLoading(true, { current: 1, total: totalPages });
              }

              if (!payload.products.length) break;
              products = products.concat(payload.products);
              page += 1;
            } while (page <= totalPages);

            allProductsCache = products;
            supplementCatalogFromProducts(products);
            return products;
          } catch (error) {
            console.error('Impossible de charger /collections/all?view=navscript:', error);
            return [];
          } finally {
            setNavscriptLoading(false);
            allProductsPromise = null;
          }
        })();

        return allProductsPromise;
      };

      const configs = [marqueSelect, modeleSelect, sousModeleSelect, anneeSelect];
      configs.forEach((config) => {
        config.trigger.addEventListener('click', () => {
          if (config.trigger.disabled) return;
          if (config.popup.hidden) openPopup(config);
          else closePopup(config, false);
        });

        if (config.searchInput) {
          config.searchInput.addEventListener('input', (event) => renderOptions(config, event.target.value));
        }

        document.addEventListener('click', (event) => {
          if (!config.popup.hidden && !config.field.contains(event.target)) closePopup(config, false);
        });

        config.panel.addEventListener('click', (event) => {
          const option = event.target.closest('.integrale-pdp__popup-option');
          if (!option) return;

          const selectedName = option.getAttribute('data-option-name') || '';
          const selectedId = option.getAttribute('data-option-id') || '';

          if (config.name === 'marque') {
            state.marque = { id: selectedId, nom: selectedName };
            config.trigger.dataset.selectedMarqueId = selectedId;
            setLabel(config, selectedName);
            resetModele();
            setTriggerState(modeleSelect, Boolean(selectedId));
          } else if (config.name === 'modele') {
            handleModeleNomSelected(selectedName);
          } else if (config.name === 'sous_modele') {
            handleSousModeleSelected(selectedName, selectedId);
          } else if (config.name === 'annee') {
            state.annee = { id: selectedName, nom: selectedName };
            config.trigger.dataset.selectedAnnee = selectedName;
            setLabel(config, selectedName);
            fetchAllProducts().then((products) => updateRedirectButton(products));
          }

          closePopup(config, true);
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !activeSelect) return;
        const activeConfig = configs.find((config) => config.name === activeSelect);
        if (activeConfig) closePopup(activeConfig, true);
      });

      const redirectButton = getRedirectButton();
      if (redirectButton) {
        redirectButton.addEventListener('click', async () => {
          const url = redirectButton.dataset.productUrl || matchedProductUrl;
          if (!url || redirectButton.disabled) return;

          const loadingEl = getIntegralePdpRoot().querySelector('[data-integrale-redirect-loading]');
          const labelEl = getIntegralePdpRoot().querySelector('[data-integrale-redirect-label]');

          redirectButton.disabled = true;
          if (loadingEl) loadingEl.hidden = false;
          if (labelEl) labelEl.classList.add('opacity-0');

          window.location.assign(url);
        });
      }

      setLabel(marqueSelect, '');
      resetModele();
      setFieldVisible(sousModeleSelect, false);

      if (marquesParseError === 'parse' || modelesParseError === 'parse') {
        showMatchMessage('parse_error');
      } else if (!marques.length) {
        showMatchMessage('meta_empty');
      }

      setTriggerState(marqueSelect, marques.length > 0);

      if (hasEmbeddedProducts && catalogPages > 1 && catalogCollection) {
        loadRemainingCatalogPages();
      } else if (!hasEmbeddedProducts) {
        fetchAllProducts().then((products) => {
          if (navscriptBlockedReason === 'password' && !products.length) {
            showMatchMessage('password');
          }
        });
      }
    }
  }

  customElements.define('integrale-bike-finder', IntegraleBikeFinder);
})();
